<template>
  <div class="animated fadeIn">
    <b-row class="mb-3">
      <b-col class="h6 text-muted mb-3">{{ identifier }}</b-col>
      <b-col v-if="sampleList.length > 1" cols="1">
        <b-button @click="removeSample" variant="danger" block>
          <i class="fa fa-trash" />
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="Test Pit" :label-cols="2" :horizontal="true">
          <b-form-input
            v-model="identifier"
            :state="!$v.identifier.$invalid"
            type="text"
            placeholder="Sample Test Pit"
            invalid-feedback="Required"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col md="2" class="pt-2">Depth</b-col>
      <b-col>
        <b-form-input
          v-model.number="depth_start"
          :state="!$v.depth_start.$invalid"
          type="number"
          placeholder="Start"
          invalid-feedback="Required"
        />
      </b-col>
      <b-col>
        <b-form-input
          v-model.number="depth_end"
          :state="!$v.depth_end.$invalid"
          type="number"
          placeholder="End"
          invalid-feedback="Required"
        />
      </b-col>
      <b-col>
        <b-form-input
          :value="sample_septic.depth_total"
          type="number"
          placeholder="Total"
          disabled
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="Sieve Analyte" :label-cols="2" :horizontal="true">
          <b-form-checkbox
            v-model="sieve"
            :value="true"
            :unchecked-value="false"
            class="pt-2"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <div>Prismatic</div>
    <b-row>
      <b-col>
        <div>SAR</div>
        <b-form-checkbox v-model="sar" :value="true" :unchecked-value="false" />
      </b-col>
      <b-col>
        <div>EC</div>
        <b-form-checkbox v-model="ec" :value="true" :unchecked-value="false" />
      </b-col>
      <b-col>
        <div>COLE</div>
        <b-form-checkbox
          v-model="cole"
          :value="true"
          :unchecked-value="false"
        />
      </b-col>
      <b-col>
        <div>Dispersion</div>
        <b-form-checkbox
          v-model="dispersion"
          :value="true"
          :unchecked-value="false"
        />
      </b-col>
    </b-row>
    <hr />
  </div>
</template>

<script>
import CustomFormCheckboxGroup from "@/components/shared/form/CustomFormCheckboxGroup";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate/src";
import { required } from "vuelidate/lib/validators";

export default {
  name: "ProjectSepticSampleFormGroup",
  components: {
    CustomFormCheckboxGroup,
  },
  mixins: [validationMixin, ToastNotifications],
  props: {
    submissionId: {
      type: String,
      default: "",
    },
    sampleId: {
      type: String,
      default: "",
    },
  },
  computed: {
    ...mapGetters({
      submission: "projectFormStepTwoSubmission",
    }),
    currentSubmission() {
      return this.submission(this.submissionId);
    },
    sampleList() {
      return this.currentSubmission.samples;
    },
    sample() {
      return this.sampleList.find(
        (sample) => sample.test_package_sample_id === this.sampleId
      );
    },
    sample_septic() {
      return this.sample.sample_septic;
    },
    identifier: {
      get() {
        return this.sample.identifier;
      },
      set(value) {
        this.handleSampleChange({ field: "identifier", value });
      },
    },
    depth_start: {
      get() {
        return this.sample_septic.depth_start;
      },
      set(value) {
        this.handleSampleChange({
          field: "sample_septic",
          value: {
            ...this.sample_septic,
            depth_start: value,
            depth_total: this.depth_end - value,
          },
        });
      },
    },
    depth_end: {
      get() {
        return this.sample_septic.depth_end;
      },
      set(value) {
        this.handleSampleChange({
          field: "sample_septic",
          value: {
            ...this.sample_septic,
            depth_end: value,
            depth_total: value - this.depth_start,
          },
        });
      },
    },
    sieve: {
      get() {
        return this.sample_septic.sieve;
      },
      set(value) {
        this.handleSampleChange({
          field: "sample_septic",
          value: {
            ...this.sample_septic,
            sieve: value,
          },
        });
      },
    },
    sar: {
      get() {
        return this.sample_septic.sar;
      },
      set(value) {
        this.handleSampleChange({
          field: "sample_septic",
          value: {
            ...this.sample_septic,
            sar: value,
          },
        });
      },
    },
    ec: {
      get() {
        return this.sample_septic.ec;
      },
      set(value) {
        this.handleSampleChange({
          field: "sample_septic",
          value: {
            ...this.sample_septic,
            ec: value,
          },
        });
      },
    },
    cole: {
      get() {
        return this.sample_septic.cole;
      },
      set(value) {
        this.handleSampleChange({
          field: "sample_septic",
          value: {
            ...this.sample_septic,
            cole: value,
          },
        });
      },
    },
    dispersion: {
      get() {
        return this.sample_septic.dispersion;
      },
      set(value) {
        this.handleSampleChange({
          field: "sample_septic",
          value: {
            ...this.sample_septic,
            dispersion: value,
          },
        });
      },
    },
  },
  validations: {
    identifier: { required },
    depth_start: { required },
    depth_end: { required },
    sieve: { required },
  },
  methods: {
    ...mapActions(["UPDATE_SUBMISSION_SAMPLE"]),
    handleSampleChange({ field, value }) {
      this.UPDATE_SUBMISSION_SAMPLE({
        submission_id: this.submissionId,
        sample_id: this.sampleId,
        sample_update: {
          ...this.sample,
          [field]: value,
        },
      });
    },
    removeSample() {
      this.$emit("updateSampleList", "remove", this.sampleId);
    },
  },
};
</script>
